import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import '../styles/ApplicantDashboard.css';

const API_BASE = window.location.port === '3000' ? 'http://localhost:5000' : '';

const formatUzbekPhoneNumber = (value) => {
  if (!value) return '+998 ';
  let digits = value.replace(/\D/g, '');
  if (digits.startsWith('998')) {
    digits = digits.slice(3);
  }
  digits = digits.slice(0, 9);
  if (digits.length === 0) return '+998 ';
  let formatted = '+998 ';
  if (digits.length > 0) formatted += `(${digits.slice(0, 2)}`;
  if (digits.length >= 2) formatted += ') ';
  if (digits.length > 2) formatted += digits.slice(2, 5);
  if (digits.length >= 5) formatted += '-';
  if (digits.length > 5) formatted += digits.slice(5, 7);
  if (digits.length >= 7) formatted += '-';
  if (digits.length > 7) formatted += digits.slice(7, 9);
  return formatted;
};

export default function ApplicantDashboard({ user, token }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  // Multi-application state
  const [selectedAppId, setSelectedAppId] = useState(() => localStorage.getItem('myApplicantId') || null);

  // Edit application modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editFiles, setEditFiles] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccessMsg, setEditSuccessMsg] = useState('');

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async (specificId) => {
    try {
      setLoading(true);
      const targetId = specificId || selectedAppId || localStorage.getItem('myApplicantId');
      const res = await axios.get('/api/applicant/profile', {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(targetId ? { 'x-applicant-id': targetId } : {})
        }
      });
      setProfile(res.data);
      if (res.data?.id) {
        setSelectedAppId(res.data.id);
        localStorage.setItem('myApplicantId', res.data.id);
      }
    } catch (err) {
      console.error('Profile fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectApplication = (app) => {
    localStorage.setItem('myApplicantId', app.id);
    setSelectedAppId(app.id);
    setProfile(prev => ({
      ...prev,
      ...app
    }));
    fetchProfile(app.id);
  };

  const initEditForm = () => {
    if (!profile) return;
    setEditForm({
      fullName: profile.fullName || profile.full_name || '',
      passportNumber: profile.passportNumber || profile.passport_number || '',
      birthDate: profile.birthDate || profile.birth_date || '',
      otmName: profile.otmName || profile.otm_name || '',
      positionAndTitle: profile.positionAndTitle || '',
      specialization: profile.specialization || '',
      dissertationTopic: profile.dissertationTopic || '',
      advisorInfo: profile.advisorInfo || '',
      phoneNumber: profile.phoneNumber || profile.phone_number || ''
    });
    setEditFiles({});
    setEditError('');
    setEditSuccessMsg('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setEditLoading(true);
      setEditError('');
      const formData = new FormData();
      Object.keys(editForm).forEach(k => {
        formData.append(k, editForm[k]);
      });
      Object.keys(editFiles).forEach(k => {
        if (editFiles[k]) {
          formData.append(k, editFiles[k]);
        }
      });

      const res = await axios.post(`/api/applicant/update/${profile.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (res.data && res.data.success) {
        setEditSuccessMsg('Arizangiz muvaffaqiyatli yangilandi va maʼmuriyatga qayta koʻrib chiqish uchun yuborildi!');
        setTimeout(() => {
          setShowEditModal(false);
          fetchProfile(profile.id);
        }, 1200);
      }
    } catch (err) {
      setEditError(err.response?.data?.error || 'Arizani yangilashda xatolik yuz berdi');
    } finally {
      setEditLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="status-badge approved">✓ Tasdiqlangan (Ruxsat berilgan)</span>;
      case 'rejected':
        return <span className="status-badge rejected">✕ Rad etilgan</span>;
      case 'passed':
        return <span className="status-badge passed">🎓 Imtihon topshirilgan (Muvaffaqiyatli)</span>;
      case 'failed':
        return <span className="status-badge failed">⚠️ Imtihondan oʻta olmagan</span>;
      default:
        return <span className="status-badge pending">⏳ Tekshiruv jarayonida (Kutilmoqda)</span>;
    }
  };

  const handlePrintSlip = () => {
    setShowPreviewModal(true);
  };

  if (loading) {
    return (
      <div className="dashboard-loading-screen">
        <div className="dash-spinner"></div>
        <p>Talabgor maʼlumotlari yuklanmoqda...</p>
      </div>
    );
  }

  // Authentic Empty State when the user has NOT submitted an application
  if (!profile || profile.hasApplication === false) {
    return (
      <div className="dashboard-page-wrapper">
        <div className="portal-breadcrumb-strip">
          <div className="container">
            <div className="breadcrumb-nav">
              <Link to="/">Bosh sahifa</Link>
              <span className="crumb-arrow">/</span>
              <span className="crumb-current">Mening arizalarim</span>
            </div>
          </div>
        </div>

        <div className="container dashboard-main-container">
          <div className="empty-application-card">
            <div className="empty-app-icon-badge">📋</div>
            <h2 className="empty-app-title">Sizda hali topshirilgan ariza mavjud emas</h2>
            <p className="empty-app-desc">
              Turon universitetida chet tilidan va mutaxassislik fanlaridan malakaviy imtihon
              topshirish uchun hoziroq onlayn ariza yuborishingiz mumkin. Arizangiz
              muvaffaqiyatli topshirilgach, uning monitoringi, ilmiy boʻlim tekshiruvi natijalari
              va imtihon yoʻllanma chiptasi ushbu sahifada avtomatik aks ettiriladi.
            </p>
            <div className="empty-app-btn-row">
              <button className="btn-empty-outline" onClick={() => navigate('/services/phd-exam')}>
                Xizmat tavsifi bilan tanishish →
              </button>
              <button className="btn-empty-primary" onClick={() => navigate('/register')}>
                Ariza topshirish
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const p = profile;

  return (
    <div className="dashboard-page-wrapper">
      <div className="portal-breadcrumb-strip">
        <div className="container">
          <div className="breadcrumb-nav">
            <Link to="/">Bosh sahifa</Link>
            <span className="crumb-arrow">/</span>
            <span>Talabgor kabineti</span>
            <span className="crumb-arrow">/</span>
            <span className="crumb-current">Ariza va imtihon monitoringi</span>
          </div>
        </div>
      </div>

      <div className="container dashboard-main-container">
        {/* Ariza tanlash paneli (Bir nechta arizasi bor talabgorlar uchun) */}
        {profile.applications && profile.applications.length > 0 && (
          <div className="applicant-applications-selector-bar">
            <div className="app-sel-header">
              <div className="app-sel-title">
                <span className="app-sel-badge">📂 Mening arizalarim ({profile.applications.length} ta)</span>
                <p>Arizalaringiz monitoringi, imtihon sanasi va natijalarini koʻrishingiz mumkin</p>
              </div>
              <Link to="/register" className="btn-add-new-application">
                + Yangi ariza topshirish
              </Link>
            </div>
            <div className="app-switcher-cards-grid">
              {profile.applications.map((app) => {
                const isSelected = String(app.id) === String(p.id);
                return (
                  <div
                    key={app.id}
                    className={`app-switcher-card ${isSelected ? 'active' : ''}`}
                    onClick={() => handleSelectApplication(app)}
                  >
                    <div className="app-switcher-top">
                      <span className="app-switcher-num">Ariza № {app.id}</span>
                      <span className={`app-switcher-status-badge ${app.status}`}>
                        {app.status === 'approved' && '✓ Tasdiqlangan'}
                        {app.status === 'pending' && '⏳ Kutilmoqda'}
                        {app.status === 'rejected' && '✕ Rad etilgan'}
                        {app.status === 'passed' && '🎓 Oʻtgan'}
                        {app.status === 'failed' && '⚠️ Oʻta olmagan'}
                      </span>
                    </div>
                    <h5 className="app-switcher-spec">{app.specialization || 'PhD malakaviy imtihoni'}</h5>
                    <div className="app-switcher-meta">
                      <span>🏛️ {app.otmName || 'Turon universiteti'}</span>
                      {app.score !== null && app.score !== undefined ? (
                        <span className="app-switcher-score">{app.score} ball</span>
                      ) : (
                        <span>{app.created_at ? new Date(app.created_at).toLocaleDateString('uz-UZ') : ''}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Talabgor haqida qisqacha maʼlumot */}
        <div className="applicant-id-banner">
          <div className="banner-user-left">
            <div className="user-avatar-hex">
              {p.fullName ? p.fullName.charAt(0) : 'A'}
            </div>
            <div className="user-primary-meta">
              <div className="name-and-status">
                <h2 className="user-name-title">{p.fullName || p.full_name}</h2>
                {getStatusBadge(p.status)}
              </div>
              <p className="user-otm-sub">
                🏛️ {p.otmName || p.otm_name || 'Turon universiteti'} • {p.positionAndTitle || 'Doktorant-tadqiqotchi'}
              </p>
            </div>
          </div>

          <div className="banner-actions-right">
            <button className="btn-dash-outline" onClick={handlePrintSlip}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
              <span>Arizani chop etish</span>
            </button>
            <button className="btn-dash-refresh" onClick={() => fetchProfile(p.id)} title="Maʼlumotlarni yangilash">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
            </button>
          </div>
        </div>

        {/* Tahrirlashga ruxsat berilgan boʻlsa ogohlantirish banner */}
        {p.allowEdit && (
          <div className="applicant-edit-alert-card">
            <div className="edit-alert-icon">✏️</div>
            <div className="edit-alert-content">
              <h4>Maʼmuriyatdan xabarnoma: Arizangizni tahrirlashingiz mumkin</h4>
              <p>
                Arizangiz yoki yuklangan hujjatlaringizda kamchiliklar aniqlangani sababli maʼmuriyat tomonidan tahrirlash imkoniyati ochildi.
              </p>
              {p.editReason && (
                <small>
                  <strong>Admin koʻrsatmasi:</strong> "{p.editReason}"
                </small>
              )}
            </div>
            <button
              type="button"
              className="btn-edit-applicant-app"
              onClick={() => {
                initEditForm();
                setShowEditModal(true);
              }}
            >
              ✏️ Arizani tahrirlash va qayta yuborish
            </button>
          </div>
        )}

        {/* Jarayon bosqichlari */}
        <div className="process-tracker-card">
          <h3 className="tracker-card-title">Ariza va imtihon bosqichlari monitoringi</h3>
          <div className="tracker-milestones">
            <div className="milestone-step done">
              <div className="milestone-dot">✓</div>
              <div className="milestone-text">
                <strong>1. Ariza yuborildi</strong>
                <span>{p.created_at ? new Date(p.created_at).toLocaleDateString('uz-UZ') : 'Hujjatlar topshirildi'}</span>
              </div>
            </div>

            <div className={`milestone-step-line ${p.status !== 'pending' ? 'active' : ''}`}></div>

            <div className={`milestone-step ${p.status === 'pending' ? 'active' : (p.status === 'rejected' ? 'rejected-step' : 'done')}`}>
              <div className="milestone-dot">
                {p.status === 'pending' ? '⏳' : (p.status === 'rejected' ? '✕' : '✓')}
              </div>
              <div className="milestone-text">
                <strong>2. Hujjatlar ekspertizasi</strong>
                <span>{p.status === 'pending' ? 'Tekshiruv jarayonida' : (p.status === 'rejected' ? 'Rad etilgan' : 'Tasdiqlandi ✓')}</span>
              </div>
            </div>

            <div className={`milestone-step-line ${p.status === 'approved' || p.status === 'passed' ? 'active' : ''}`}></div>

            <div className={`milestone-step ${p.status === 'approved' ? 'active' : (p.status === 'passed' ? 'done' : '')}`}>
              <div className="milestone-dot">{p.status === 'approved' ? '📅' : (p.status === 'passed' ? '✓' : '3')}</div>
              <div className="milestone-text">
                <strong>3. Imtihon ruxsati</strong>
                <span>{p.status === 'approved' ? 'Offline sana belgilandi' : (p.status === 'passed' ? 'Ruxsat berilgan' : 'Kutilmoqda')}</span>
              </div>
            </div>

            <div className={`milestone-step-line ${p.status === 'passed' ? 'active' : ''}`}></div>

            <div className={`milestone-step ${p.status === 'passed' ? 'done passed-step' : (p.status === 'failed' ? 'rejected-step' : (p.status === 'approved' ? 'active' : ''))}`}>
              <div className="milestone-dot">{p.status === 'passed' ? '🏆' : (p.status === 'failed' ? '⚠️' : (p.status === 'approved' ? '4' : '4'))}</div>
              <div className="milestone-text">
                <strong>4. Offline imtihon va natija</strong>
                <span>
                  {p.status === 'passed'
                    ? `✓ Muvaffaqiyatli topshirildi (${p.score !== null && p.score !== undefined ? p.score : (p.examResult?.score || 85)} ball)`
                    : p.status === 'failed'
                    ? `Sinovdan oʻta olmadi (${p.score !== null && p.score !== undefined ? p.score : (p.examResult?.score || 45)} ball)`
                    : p.status === 'approved'
                    ? `Offline imtihon: ${p.testDate || p.test_date || '2026-10-25'}`
                    : 'Ruxsat kutilmoqda'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* OFFLINE IMTIHON NATIJASI VA KOMISSIYA BAYONNOMASI (Admin kiritgan natija) */}
        {(p.status === 'passed' || p.status === 'failed' || p.examResult) && (
          <div className={`exam-result-showcase-card ${p.status === 'passed' ? 'passed' : 'failed'}`}>
            <div className="ers-header">
              <div className="ers-badge-icon">
                {p.status === 'passed' ? '🏆' : '⚠️'}
              </div>
              <div className="ers-title-block">
                <span className="ers-pill">OFFLINE MALAKAVIY IMTIHON NATIJASI</span>
                <h3>
                  {p.status === 'passed'
                    ? 'Tabriklaymiz! Malakaviy imtihon sinovidan muvaffaqiyatli oʻtdingiz'
                    : 'Malakaviy imtihon sinovidan oʻtish bali toʻplanmadi'}
                </h3>
                <span className="ers-sub">Turon universiteti malakaviy imtihon komissiyasi rasmiy qaydnomasi</span>
              </div>
              <div className="ers-score-box">
                <span className="score-digit">{p.score !== null && p.score !== undefined ? p.score : (p.examResult?.score || 85)}</span>
                <span className="score-denom">/ 100 ball</span>
              </div>
            </div>

            <div className="ers-metrics-grid">
              <div className="ers-metric-cell">
                <span className="m-lbl">Imtihon natijasi:</span>
                <span className={`m-val status ${p.status === 'passed' ? 'pass' : 'fail'}`}>
                  {p.status === 'passed' ? '✓ SINOVDAN OʻTGAN (Ijobiy)' : '✕ SINOVDAN OʻTA OLMAGAN'}
                </span>
              </div>
              <div className="ers-metric-cell">
                <span className="m-lbl">Rasmiy qaydnoma raqami:</span>
                <span className="m-val mono">
                  {(() => {
                    const raw = p.certificateNumber || p.examResult?.certificateNumber || '2026-041';
                    const clean = String(raw).replace(/^[№#\s]+/, '').trim();
                    return `№ ${clean}`;
                  })()}
                </span>
              </div>
              <div className="ers-metric-cell">
                <span className="m-lbl">Topshirilgan sana:</span>
                <span className="m-val">📅 {p.testDate || p.examResult?.examDate || new Date().toLocaleDateString('uz-UZ')}</span>
              </div>
              <div className="ers-metric-cell">
                <span className="m-lbl">Imtihon fani:</span>
                <span className="m-val highlight">{p.specialization}</span>
              </div>
            </div>

            <div className="ers-verdict-box">
              <div className="verdict-title">📋 Komissiya hayʼati xulosasi:</div>
              <p className="verdict-text">
                "{p.examResult?.resultNotes || (p.status === 'passed'
                  ? 'Talabgor malakaviy imtihon dasturi boʻyicha berilgan barcha savollarga toʻliq, ilmiy asoslangan holda javob berdi. Hayʼat qarori bilan ijobiy baholandi.'
                  : 'Talabgor oʻtish bali (kamida 60%)ni toʻplay olmadi. Qayta topshirish boʻyicha ilmiy boʻlimga murojaat qiling.')}"
              </p>
            </div>

            {/* OFFLINE IMTIHON BAYONNOMASI (PDF) KOʻRISH VA YUKLAB OLISH */}
            {(() => {
              const resFile = p.examResult?.resultFile || p.resultFile;
              if (resFile) {
                return (
                  <div className="ers-protocol-file-box">
                    <div className="protocol-file-info">
                      <span className="p-icon">📑</span>
                      <div>
                        <strong>Rasmiy imtihon bayonnomasi va komissiya qaydnomasi (PDF)</strong>
                        <span className="p-filename">📁 {resFile}</span>
                      </div>
                    </div>
                    <div className="protocol-file-actions">
                      <button
                        type="button"
                        className="btn-p-view"
                        onClick={() => setPreviewDoc({
                          title: 'Malakaviy imtihon rasmiy bayonnomasi',
                          filename: resFile,
                          url: `${API_BASE}/api/documents/view/${encodeURIComponent(resFile)}`,
                          downloadUrl: `${API_BASE}/api/documents/download/${encodeURIComponent(resFile)}`
                        })}
                      >
                        👁️ PDF faylni koʻrish
                      </button>
                      <a
                        href={`${API_BASE}/api/documents/download/${encodeURIComponent(resFile)}`}
                        download={resFile}
                        className="btn-p-download"
                      >
                        📥 Bayonnomani yuklab olish (PDF)
                      </a>
                    </div>
                  </div>
                );
              }
              return (
                <div className="ers-protocol-file-box ers-protocol-empty" style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}>
                  <div className="protocol-file-info">
                    <span className="p-icon" style={{ background: '#e2e8f0', color: '#64748b' }}>ℹ️</span>
                    <div>
                      <strong style={{ color: '#334155' }}>Rasmiy bayonnoma tizimda elektron tasdiqlangan</strong>
                      <span className="p-filename" style={{ color: '#64748b' }}>
                        Komissiya baholash bayonnomasi № {String(p.certificateNumber || p.examResult?.certificateNumber || '2026-041').replace(/^[№#\s]+/, '')} bilan qayd etilgan.
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Imtihonga ruxsat berilgan holatdagi chipta (Offline imtihon) */}
        {(p.status === 'approved' || p.status === 'passed') && (
          <div className="exam-ticket-card">
            <div className="ticket-header">
              <div className="ticket-title-group">
                <span className="ticket-badge">RASMIY OFFLINE IMTIHON YOʻLLANMASI</span>
                <h3>Malakaviy imtihon sinovi belgilandi (Anʼanaviy / Offline)</h3>
              </div>
              <div className="ticket-code">
                <span>Ariza raqami: № {p.id}</span>
              </div>
            </div>

            <div className="ticket-details-grid">
              <div className="ticket-detail-item">
                <span className="td-label">Imtihon fani:</span>
                <span className="td-value highlight">{p.specialization}</span>
              </div>
              <div className="ticket-detail-item">
                <span className="td-label">Belgilangan sana:</span>
                <span className="td-value">📅 {p.testDate || p.test_date || '2026-10-25'}</span>
              </div>
              <div className="ticket-detail-item">
                <span className="td-label">Boshlanish vaqti:</span>
                <span className="td-value">⏰ {p.testTime || p.test_time || '09:00'} (Toshkent vaqti)</span>
              </div>
              <div className="ticket-detail-item">
                <span className="td-label">Offline oʻtkazilish joyi / Bino:</span>
                <span className="td-value">{p.examRoom || p.exam_room || 'Turon universiteti bosh binosi, 214-auditoriya (Qarshi shahri, Nasaf koʻchasi, 1/14-uy)'}</span>
              </div>
            </div>

            <div className="offline-instruction-box">
              <div className="instruction-header">
                <span className="inst-icon">🏛️</span>
                <strong>DIQQAT: Malakaviy imtihon Turon universitetida OFFLINE (anʼanaviy) tarzda boʻlib oʻtadi</strong>
              </div>
              <p className="instruction-text">
                {p.examNotes || p.exam_notes || "Talabgor belgilangan sana va vaqtda yuqorida koʻrsatilgan auditoriyaga shaxsni tasdiqlovchi hujjat (pasport yoki ID-karta) bilan 15 daqiqa oldin yetib kelishi hamda imtihon komissiyasi huzurida anʼanaviy shaklda topshirishi lozim."}
              </p>
              <div className="offline-key-points">
                <span>✓ Shaxsni tasdiqlovchi asl pasport / ID-karta bilan kelish shart</span>
                <span>✓ Imtihon offline yozma va ogʻzaki tartibda oʻtkaziladi</span>
                <span>✓ Natijalar komissiya bayonnomasi bilan eʼlon qilinadi</span>
              </div>
            </div>

            <div className="ticket-cta-row">
              <button
                className="btn-print-voucher-action"
                onClick={handlePrintSlip}
                title="Yoʻllanma chiptasini chop etish yoki PDF sifatida saqlash"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
                <span>🖨️ RASMIY YOʻLLANMA CHIPTASINI CHOP ETISH / YUKLAB OLISH</span>
              </button>
            </div>
          </div>
        )}

        {p.status === 'pending' && (
          <div className="status-notice-card pending">
            <div className="notice-icon">⏳</div>
            <div className="notice-content">
              <h4>Arizangiz koʻrib chiqish jarayonida (Ekspertiza)</h4>
              <p>
                Turon universiteti Ilmiy boʻlimi masʼul komissiyasi tomonidan taqdim etilgan hujjatlar
                (OAK byulleteni, doktorantura maʼlumotnomasi, pasport) ekspertizadan oʻtkazilmoqda.
                Arizangiz tasdiqlangach, ushbu sahifada sizga offline imtihon sanasi, vaqti, oʻtkaziladigan bino va auditoriya aks ettiriladi.
              </p>
            </div>
          </div>
        )}

        {p.status === 'rejected' && (
          <div className="status-notice-card rejected">
            <div className="notice-icon">❌</div>
            <div className="notice-content">
              <h4>Ariza ilmiy komissiya tomonidan rad etildi</h4>
              <div className="reject-reason-box">
                <span className="reject-lbl">Komissiya izohi / Rad etish sababi:</span>
                <p className="reject-reason-text">
                  "{p.rejectionReason || p.rejection_reason || 'Yuklangan hujjatlar toʻliq emas yoki OAK talablariga javob bermaydi.'}"
                </p>
              </div>
              <div className="reject-action-guidance">
                <span>Kamchiliklarni bartaraf etib, arizangizni qayta topshirishingiz mumkin:</span>
                <button className="btn-reapply" onClick={() => navigate('/register')}>
                  Arizani qayta topshirish →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Anketa va tadqiqot maʼlumotlari */}
        <div className="dashboard-grid-2col">
          {/* Ustun 1 */}
          <div className="dash-info-card">
            <div className="card-head">
              <h3>📋 Talabgor anketasi maʼlumotlari</h3>
            </div>
            <div className="card-body">
              <div className="meta-pair-row">
                <span className="mp-label">F.I.Sh.:</span>
                <span className="mp-value">{p.fullName || p.full_name}</span>
              </div>
              <div className="meta-pair-row">
                <span className="mp-label">Tugʻilgan sana:</span>
                <span className="mp-value">{p.birthDate || p.birth_date}</span>
              </div>
              <div className="meta-pair-row">
                <span className="mp-label">JSHSHIR raqami:</span>
                <span className="mp-value mono">{p.passportNumber || p.passport_number}</span>
              </div>
              <div className="meta-pair-row">
                <span className="mp-label">Telefon raqami:</span>
                <span className="mp-value">{p.phoneNumber || p.phone_number}</span>
              </div>
              <div className="meta-pair-row">
                <span className="mp-label">Elektron pochta:</span>
                <span className="mp-value">{p.email}</span>
              </div>
              <div className="meta-pair-row">
                <span className="mp-label">Ariza beruvchi turi:</span>
                <span className="mp-value">{p.applicantType || 'Ushbu taʼlim muassasasi'}</span>
              </div>
            </div>
          </div>

          {/* Ustun 2 */}
          <div className="dash-info-card">
            <div className="card-head">
              <h3>🎓 Ilmiy tadqiqot maʼlumotlari</h3>
            </div>
            <div className="card-body">
              <div className="meta-pair-row">
                <span className="mp-label">Imtihon fani:</span>
                <span className="mp-value highlight-blue">{p.specialization}</span>
              </div>
              <div className="meta-pair-row">
                <span className="mp-label">Biriktirilgan OTM/ITM:</span>
                <span className="mp-value">{p.otmName || p.otm_name}</span>
              </div>
              <div className="meta-pair-row">
                <span className="mp-label">Ish joyi va lavozimi:</span>
                <span className="mp-value">{p.positionAndTitle}</span>
              </div>
              <div className="meta-pair-row">
                <span className="mp-label">Dissertatsiya mavzusi:</span>
                <span className="mp-value italic">"{p.dissertationTopic}"</span>
              </div>
              <div className="meta-pair-row">
                <span className="mp-label">Ilmiy maslahatchi/rahbar:</span>
                <span className="mp-value">{p.advisorInfo}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Taqdim etilgan rasmiy hujjatlar */}
        <div className="uploaded-docs-card">
          <div className="card-head">
            <h3>📑 Taqdim etilgan rasmiy hujjatlar</h3>
            <span className="doc-count-badge">Tizimga biriktirilgan hujjatlar</span>
          </div>

          <div className="docs-grid-list">
            {[
              { key: 'application', title: 'Malakaviy imtihon arizasi', icon: '📝', defaultFile: `ariza_${p.id}.pdf` },
              { key: 'oakBulletin', title: 'OAK Byulleteni koʻchirmasi', icon: '📰', defaultFile: `oak_bulletin_${p.id}.pdf` },
              { key: 'phd_certificate', title: 'Doktorantura maʼlumotnomasi', icon: '🏛️', defaultFile: `doktorantura_guvohnoma_${p.id}.pdf` },
              { key: 'passport', title: 'Pasport / ID-karta nusxasi', icon: '🪪', defaultFile: `passport_${p.id}.pdf` },
              ...(p.applicantType !== 'Ushbu taʼlim muassasasi' || (p.documents && p.documents.paymentReceipt) ? [
                { key: 'paymentReceipt', title: 'Toʻlov kvitansiyasi (BHM 50%)', icon: '💳', defaultFile: `tolov_kvitansiyasi_${p.id}.pdf` }
              ] : []),
              ...(p.documents && p.documents.referralLetter ? [
                { key: 'referralLetter', title: 'Muassasadan yoʻllanma xat', icon: '📑', defaultFile: `yollanma_xat_${p.id}.pdf` }
              ] : [])
            ].map(docDef => {
              const docFilename = (p.documents && p.documents[docDef.key]) || docDef.defaultFile;
              const docViewUrl = `${API_BASE}/api/documents/view/${encodeURIComponent(docFilename)}`;
              const docDownloadUrl = `${API_BASE}/api/documents/download/${encodeURIComponent(docFilename)}`;
              return (
                <div className="doc-item-box" key={docDef.key}>
                  <div className="doc-card-top-content">
                    <div className="doc-card-header-row">
                      <span className="doc-card-icon">{docDef.icon}</span>
                      <div className="doc-card-title-group">
                        <h4 className="doc-title">{docDef.title}</h4>
                        <span className="doc-status-ok">✓ Hujjat yuklangan</span>
                      </div>
                    </div>
                    <div className="doc-file-info-chip" title={docFilename}>
                      <span className="doc-file-name-sub">📁 {docFilename}</span>
                    </div>
                  </div>
                  <div className="doc-actions-grid">
                    <button
                      type="button"
                      className="btn-applicant-doc-view"
                      onClick={() => setPreviewDoc({
                        title: docDef.title,
                        filename: docFilename,
                        url: docViewUrl,
                        downloadUrl: docDownloadUrl
                      })}
                      title="Hujjatni ochib koʻrish"
                    >
                      👁️ Koʻrish
                    </button>
                    <a
                      href={docDownloadUrl}
                      download={docFilename}
                      className="btn-applicant-doc-download"
                      title="Hujjatni yuklab olish"
                    >
                      📥 Yuklab olish
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Printable Pass Modal Preview & Actual Printable Sheet */}
        {showPreviewModal && (
          <div className="print-modal-backdrop" onClick={() => setShowPreviewModal(false)}>
            <div className="print-modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="print-modal-toolbar no-print">
                <div className="toolbar-info">
                  <span className="toolbar-badge">🖨️ Chop etish koʻrinishi</span>
                  <span className="toolbar-sub">Rasmiy A4 formati boʻyicha yoʻllanma varaqasi</span>
                </div>
                <div className="toolbar-btns">
                  <button className="btn-print-action" onClick={() => window.print()}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
                    <span>Chop etish (Print)</span>
                  </button>
                  <button className="btn-close-modal" onClick={() => setShowPreviewModal(false)}>
                    ✕ Yopish
                  </button>
                </div>
              </div>

              {/* Official Voucher Inside Modal (and printed via #printable-slip) */}
              <div className="print-sheet-wrapper">
                <div id="printable-slip" className="official-print-voucher">
                  <div className="print-header">
                    <div className="print-header-left">
                      <img src="/logo.png" alt="Turon University" className="print-logo" />
                      <div className="print-header-titles">
                        <h2 className="print-univ-name">TURON UNIVERSITETI</h2>
                        <p className="print-ministry">OʻZBEKISTON RESPUBLIKASI TAʼLIM VA ILM-FAN VAZIRLIGI</p>
                        <p className="print-dep">Ilmiy boʻlim va doktorantura kengashi</p>
                      </div>
                    </div>
                    <div className="print-header-right">
                      <div className="print-voucher-qr">
                        <div className="qr-real-wrapper">
                          <QRCodeSVG
                            value={`${window.location.origin}/verify-voucher/${p.id || 1}`}
                            size={92}
                            level="M"
                            includeMargin={false}
                          />
                          <span className="qr-code-sub">№ {p.id || 1} • QR-VERIFIED</span>
                        </div>
                      </div>
                      <div className="print-app-num">
                        <strong>Ariza raqami:</strong> № {p.id || 1}
                      </div>
                      <div className="print-app-date">
                        <strong>Sana:</strong> {new Date().toLocaleDateString('uz-UZ')}
                      </div>
                    </div>
                  </div>

                  <div className="print-title-banner">
                    <h1>DOKTORANTURA VA MALAKAVIY IMTIHON SINOVIGA RASMIY YOʻLLANMA VARAQASI</h1>
                    <p className="print-slip-subtitle">Talabgor anketasi va rasmiy ruxsatnoma hujjati</p>
                  </div>

                  <div className="print-section">
                    <h3 className="print-sec-title">1. TALABGOR SHAXSIY VA ANKETA MAʼLUMOTLARI</h3>
                    <table className="print-table">
                      <tbody>
                        <tr>
                          <td className="print-lbl">F.I.Sh. (Toʻliq):</td>
                          <td className="print-val"><strong>{p.fullName || p.full_name}</strong></td>
                          <td className="print-lbl">Tugʻilgan sana:</td>
                          <td className="print-val">{p.birthDate || p.birth_date || '—'}</td>
                        </tr>
                        <tr>
                          <td className="print-lbl">JSHSHIR raqami:</td>
                          <td className="print-val mono">{p.passportNumber || p.passport_number || '—'}</td>
                          <td className="print-lbl">Telefon raqami:</td>
                          <td className="print-val">{p.phoneNumber || p.phone_number || '—'}</td>
                        </tr>
                        <tr>
                          <td className="print-lbl">Elektron pochta:</td>
                          <td className="print-val">{p.email || '—'}</td>
                          <td className="print-lbl">Talabgor toifasi:</td>
                          <td className="print-val">{p.applicantType || p.applicant_type || 'Ushbu taʼlim muassasasi'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="print-section">
                    <h3 className="print-sec-title">2. ILMIY TADQIQOT VA DISSERTATSIYA MAʼLUMOTLARI</h3>
                    <table className="print-table">
                      <tbody>
                        <tr>
                          <td className="print-lbl">Biriktirilgan muassasa (OTM):</td>
                          <td className="print-val" colSpan="3">{p.otmName || p.otm_name || 'Turon universiteti'} ({p.positionAndTitle || 'Doktorant-tadqiqotchi'})</td>
                        </tr>
                        <tr>
                          <td className="print-lbl">Imtihon fani / Ixtisoslik:</td>
                          <td className="print-val" colSpan="3"><strong>{p.specialization || 'Ingliz tili'}</strong></td>
                        </tr>
                        <tr>
                          <td className="print-lbl">Dissertatsiya mavzusi:</td>
                          <td className="print-val italic" colSpan="3">«{p.dissertationTopic || 'Mavzu kiritilmagan'}»</td>
                        </tr>
                        <tr>
                          <td className="print-lbl">Ilmiy rahbar / maslahatchi:</td>
                          <td className="print-val" colSpan="3">{p.advisorInfo || '—'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="print-section">
                    <h3 className="print-sec-title">3. IMTIHON TASHKILIY MAʼLUMOTLARI VA AUDITORIYA</h3>
                    <table className="print-table">
                      <tbody>
                        <tr>
                          <td className="print-lbl">Ariza holati:</td>
                          <td className="print-val">
                            {p.status === 'approved' ? '✓ TASDIQLANGAN (RUXSAT BERILGAN)' :
                             p.status === 'passed' ? '🏆 SINOVDAN OʻTGAN' :
                             p.status === 'rejected' ? '✕ RAD ETILGAN' : '⏳ TEKSHIRUV JARAYONIDA'}
                          </td>
                          <td className="print-lbl">Belgilangan sana:</td>
                          <td className="print-val"><strong>{p.testDate || p.test_date || 'Tekshiruv yakunlangach belgilanadi'}</strong></td>
                        </tr>
                        <tr>
                          <td className="print-lbl">Boshlanish vaqti:</td>
                          <td className="print-val">{p.testTime || p.test_time ? `${p.testTime || p.test_time} (Toshkent vaqti)` : '—'}</td>
                          <td className="print-lbl">Oʻtish balli:</td>
                          <td className="print-val">60% (Kamida 6 ta toʻgʻri javob)</td>
                        </tr>
                        <tr>
                          <td className="print-lbl">Manzil va auditoriya:</td>
                          <td className="print-val" colSpan="3">
                            {p.examRoom || p.exam_room || 'Turon universiteti bosh binosi, 214-auditoriya (Qarshi shahri, Nasaf koʻchasi, 1/14-uy)'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="print-section print-rules-box">
                    <h4 className="rules-heading">⚠️ IMTIHON SINOVI QOIDALARI VA TALABLAR:</h4>
                    <ol className="rules-list">
                      <li>Talabgor belgilangan vaqtdan 15 daqiqa oldin shaxsini tasdiqlovchi hujjat (pasport yoki ID-karta)ning asl nusxasi bilan kelishi shart.</li>
                      <li>Malakaviy imtihon Turon universitetida offline (anʼanaviy) tartibda komissiya huzurida oʻtkaziladi.</li>
                      <li>Sinov vaqtida uyali aloqa vositalari, konspektlar va begona yordamchi vositalardan foydalanish qatʼiyan man etiladi.</li>
                    </ol>
                  </div>

                  <div className="print-signatures-row">
                    <div className="sig-col">
                      <span className="sig-label">Talabgor:</span>
                      <div className="sig-line">___________________________</div>
                      <span className="sig-sub">({p.fullName || p.full_name})</span>
                    </div>

                    <div className="sig-col stamp-box">
                      <div className="stamp-circle">
                        <span>M.Oʻ.</span>
                        <small>Turon universiteti<br/>Ilmiy boʻlimi</small>
                      </div>
                    </div>

                    <div className="sig-col">
                      <span className="sig-label">Masʼul kotib:</span>
                      <div className="sig-line">___________________________</div>
                      <span className="sig-sub">(Ilmiy kengash kotibi)</span>
                    </div>
                  </div>

                  <div className="print-verification-notice">
                    <span>📱 Skanerlanganda verifikatsiya va yuklab olish manzili: </span>
                    <strong className="mono-qr-url">{`${window.location.origin}/verify-voucher/${p.id || 1}`}</strong>
                  </div>

                  <div className="print-footer-strip">
                    <span>Turon universiteti | Rasmiy sayt: https://turonuniversity.uz/ru# | Aloqa: +998 (75) 220-00-25 | Qarshi sh., Nasaf koʻchasi, 1/14</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* DOCUMENT PREVIEW MODAL */}
        {previewDoc && (
          <div className="print-modal-backdrop" onClick={() => setPreviewDoc(null)}>
            <div className="print-modal-container doc-preview-modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="print-modal-toolbar no-print">
                <div className="toolbar-info">
                  <span className="toolbar-badge">📑 {previewDoc.title}</span>
                  <span className="toolbar-sub">Fayl: {previewDoc.filename}</span>
                </div>
                <div className="toolbar-btns">
                  <a
                    href={previewDoc.downloadUrl || previewDoc.url}
                    download={previewDoc.filename}
                    className="btn-print-action btn-doc-download-act"
                    style={{ textDecoration: 'none' }}
                  >
                    📥 Yuklab olish
                  </a>
                  <a
                    href={previewDoc.url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-print-action"
                    style={{ textDecoration: 'none' }}
                  >
                    ↗ Alohida oynada ochish
                  </a>
                  <button className="btn-close-modal" onClick={() => setPreviewDoc(null)}>
                    ✕ Yopish
                  </button>
                </div>
              </div>

              <div className="doc-preview-iframe-wrapper" style={{ height: '75vh', width: '100%', position: 'relative' }}>
                <object
                  data={previewDoc.url}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                >
                  <iframe
                    src={previewDoc.url}
                    title={previewDoc.title}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  />
                </object>
              </div>
            </div>
          </div>
        )}

        {/* APPLICANT EDIT APPLICATION MODAL */}
        {showEditModal && (
          <div className="admin-modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="app-edit-modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
                <h3 style={{ color: '#0f2b5c', margin: 0, fontSize: '18px', fontWeight: '800' }}>
                  ✏️ Ariza № {p.id} maʼlumotlarini tahrirlash
                </h3>
                <button
                  type="button"
                  className="btn-close-modal"
                  style={{ color: '#334155', background: '#f1f5f9', border: '1px solid #cbd5e1' }}
                  onClick={() => setShowEditModal(false)}
                >
                  ✕
                </button>
              </div>

              {p.editReason && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>
                  <strong>Admin koʻrsatmasi:</strong> {p.editReason}
                </div>
              )}

              {editError && (
                <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>
                  ⚠️ {editError}
                </div>
              )}

              {editSuccessMsg && (
                <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#059669', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>
                  ✓ {editSuccessMsg}
                </div>
              )}

              <form onSubmit={handleEditSubmit} className="app-edit-modal-body">
                <div className="form-grid-2col">
                  <div className="form-group-modal">
                    <label>F.I.Sh. (Toʻliq ism sharifingiz):</label>
                    <input
                      type="text"
                      value={editForm.fullName || ''}
                      onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group-modal">
                    <label>Telefon raqami:</label>
                    <input
                      type="tel"
                      inputMode="tel"
                      maxLength={19}
                      value={editForm.phoneNumber || ''}
                      onChange={(e) => setEditForm({ ...editForm, phoneNumber: formatUzbekPhoneNumber(e.target.value) })}
                      placeholder="+998 (90) 123-45-67"
                      required
                    />
                  </div>

                  <div className="form-group-modal">
                    <label>JSHSHIR raqami:</label>
                    <input
                      type="text"
                      maxLength="14"
                      value={editForm.passportNumber || ''}
                      onChange={(e) => setEditForm({ ...editForm, passportNumber: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group-modal">
                    <label>Tugʻilgan sana:</label>
                    <input
                      type="date"
                      value={editForm.birthDate || ''}
                      onChange={(e) => setEditForm({ ...editForm, birthDate: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group-modal">
                    <label>Biriktirilgan OTM / ITM:</label>
                    <input
                      type="text"
                      value={editForm.otmName || ''}
                      onChange={(e) => setEditForm({ ...editForm, otmName: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group-modal">
                    <label>Ish joyi va lavozimi:</label>
                    <input
                      type="text"
                      value={editForm.positionAndTitle || ''}
                      onChange={(e) => setEditForm({ ...editForm, positionAndTitle: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-group-modal full-width">
                  <label>Dissertatsiya mavzusi:</label>
                  <input
                    type="text"
                    value={editForm.dissertationTopic || ''}
                    onChange={(e) => setEditForm({ ...editForm, dissertationTopic: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group-modal full-width">
                  <label>Ilmiy maslahatchi / rahbar:</label>
                  <input
                    type="text"
                    value={editForm.advisorInfo || ''}
                    onChange={(e) => setEditForm({ ...editForm, advisorInfo: e.target.value })}
                    required
                  />
                </div>

                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #cbd5e1' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '14.5px', color: '#0f2b5c' }}>
                    📑 Hujjatlarni yangilash (Agar kerak boʻlsa yangi fayl tanlang):
                  </h4>
                  <div className="form-grid-2col">
                    <div className="form-group-modal">
                      <label>Pasport / ID-karta nusxasi (PDF/Rasm):</label>
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => setEditFiles({ ...editFiles, passport: e.target.files[0] })}
                      />
                    </div>

                    <div className="form-group-modal">
                      <label>OAK Byulleteni nusxasi (PDF):</label>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setEditFiles({ ...editFiles, oakBulletin: e.target.files[0] })}
                      />
                    </div>

                    <div className="form-group-modal">
                      <label>Doktorantura maʼlumotnomasi (PDF):</label>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setEditFiles({ ...editFiles, phd_certificate: e.target.files[0] })}
                      />
                    </div>

                    <div className="form-group-modal">
                      <label>Ariza hujjati (PDF):</label>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setEditFiles({ ...editFiles, application: e.target.files[0] })}
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #e2e8f0' }}>
                  <button
                    type="button"
                    className="btn-modal-cancel"
                    onClick={() => setShowEditModal(false)}
                    disabled={editLoading}
                  >
                    Bekor qilish
                  </button>
                  <button
                    type="submit"
                    className="btn-modal-save"
                    disabled={editLoading}
                  >
                    {editLoading ? 'Saqlanmoqda...' : 'Saqlash va maʼmuriyatga yuborish'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
